// src/pages/Messages.jsx
// メッセージ（/messages と /messages/:conversationId。全ロール）【2026-10-16】
//   広い画面: 左に会話の一覧、右に会話。800px 以下: どちらか一方（会話には「戻る」）
//   一覧はリアルタイムの合図・15秒ごと・窓が前に出たときに取り直す
//   学生: 「新しい会話」「届いた申請」。全員: 設定（既読・ブロック中の人）
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { msgApi } from '../messaging/api';
import { useFeatures, useRealtime, useWindowFocus } from '../messaging/util';
import ConversationList from '../messaging/ConversationList';
import ConversationView from '../messaging/ConversationView';
import NewConversationPanel from '../messaging/NewConversationPanel';
import RequestsPanel from '../messaging/RequestsPanel';
import MessageSettings from '../messaging/MessageSettings';
import MsgIcon from '../messaging/MsgIcon';
import Icon from '../components/Icon';
import '../messaging/messaging.css';

export default function Messages({ user }) {
  const { conversationId } = useParams();
  const activeId = conversationId && /^\d+$/.test(conversationId) ? Number(conversationId) : null;
  const navigate = useNavigate();
  const { features, suspended_until: suspendedUntil, loaded: featuresLoaded } = useFeatures();
  const isStudent = user.role === 'student';
  const isTeacher = user.role === 'teacher' || user.role === 'developer';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showStopped, setShowStopped] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);       // 先生: 未対応だけ
  const [requests, setRequests] = useState(null);
  const [panel, setPanel] = useState(null);              // new / requests / settings
  const focused = useWindowFocus();
  const timer = useRef(null);

  const loadList = useCallback(async () => {
    const r = await msgApi.get('/conversations' + (showStopped ? '' : '?status=active'));
    setLoading(false);
    if (r.success) { setItems(r.conversations); setErr(''); } else setErr(r.message || '会話を読み込めませんでした');
  }, [showStopped]);

  const loadRequests = useCallback(async () => {
    if (!isStudent) return;
    const r = await msgApi.get('/requests');
    setRequests(r.success ? r.requests : []);
  }, [isStudent]);

  /** 続けて合図が来たときにまとめる */
  const reload = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; loadList(); }, 400);
    try { window.dispatchEvent(new Event('messages-changed')); } catch (_) { /* 未読のバッジを合わせる */ }
  }, [loadList]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadRequests(); }, [loadRequests]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadList(); loadRequests();
    }, 15000);
    return () => clearInterval(t);
  }, [loadList, loadRequests]);
  useEffect(() => { if (focused) { loadList(); loadRequests(); } }, [focused]); // eslint-disable-line react-hooks/exhaustive-deps -- 窓が前に出たときだけ

  useRealtime((ev) => {
    if (ev.type === 'message' || ev.type === 'conversation' || ev.type === 'read') reload();
    if (ev.type === 'conversation') loadRequests();
  });

  const open = useCallback((id) => navigate(`/messages/${id}`), [navigate]);
  const back = useCallback(() => navigate('/messages'), [navigate]);

  const visible = onlyOpen ? items.filter(c => c.kind === 'teacher_dm' && c.handled === false) : items;
  const summary = items.find(c => c.id === activeId) || null;
  const hasTeacherDm = items.some(c => c.handled !== undefined);
  const pending = requests ? requests.length : 0;
  const canStart = isStudent && featuresLoaded
    && (features.teacher_dm || features.class_chat || features.student_chat || user.isDeveloper);

  return (
    <div className="page-container msg-page">
      <div className={'msg-shell' + (activeId ? ' has-active' : '')}>
        <aside className="msg-list-pane">
          <div className="msg-list-head">
            <h1>メッセージ</h1>
            <div className="msg-list-head-actions">
              {isStudent && (
                <button type="button" className="msg-icon-btn msg-with-badge" onClick={() => setPanel('requests')}
                        aria-label={`届いた申請${pending ? ` ${pending}件` : ''}`} title="届いた申請">
                  <MsgIcon name="inbox" size={20} />
                  {pending > 0 && <span className="msg-badge dot">{pending > 9 ? '9+' : pending}</span>}
                </button>
              )}
              <button type="button" className="msg-icon-btn" onClick={() => setPanel('settings')} aria-label="メッセージの設定" title="設定">
                <Icon name="settings" size={20} />
              </button>
              {canStart && (
                <button type="button" className="btn-primary msg-new-btn" onClick={() => setPanel('new')}>
                  <Icon name="plus" size={16} /> 新しい会話
                </button>
              )}
            </div>
          </div>
          <div className="msg-list-filters">
            <label className="msg-check-label">
              <input type="checkbox" checked={showStopped} onChange={e => setShowStopped(e.target.checked)} />
              停止した会話も表示
            </label>
            {isTeacher && hasTeacherDm && (
              <label className="msg-check-label">
                <input type="checkbox" checked={onlyOpen} onChange={e => setOnlyOpen(e.target.checked)} />
                未対応だけ
              </label>
            )}
          </div>
          {err && <p className="msg-error" style={{ padding: '0 1rem' }}>{err}</p>}
          <ConversationList items={visible} activeId={activeId} meId={user.id} onOpen={open} loading={loading}
                            emptyText={onlyOpen ? '未対応のメッセージはありません' : isStudent ? '会話はまだありません。「新しい会話」から始められます' : '会話はまだありません'} />
        </aside>
        <section className="msg-conv-pane">
          {activeId ? (
            <ConversationView key={activeId} conversationId={activeId} user={user} summary={summary}
                              features={features} featuresLoaded={featuresLoaded} suspendedUntil={suspendedUntil}
                              onBack={back} onChanged={reload} onOpenConversation={open} />
          ) : (
            <div className="msg-view-empty msg-placeholder">
              <MsgIcon name="message" size={40} />
              <p>左の一覧から会話を選んでください</p>
            </div>
          )}
        </section>
      </div>

      {panel === 'new' && (
        <NewConversationPanel features={features} isDeveloper={!!user.isDeveloper} conversations={items}
                              onClose={() => setPanel(null)} onOpen={(id) => { loadList(); open(id); }} />
      )}
      {panel === 'requests' && (
        <RequestsPanel requests={requests} onClose={() => setPanel(null)} onReload={() => { loadRequests(); loadList(); }}
                       onOpen={(id) => { loadList(); open(id); }} />
      )}
      {panel === 'settings' && (
        <MessageSettings isStudent={isStudent} onClose={() => setPanel(null)} onChanged={reload} />
      )}
    </div>
  );
}
